import {
  Megaphone,
  Blocks,
  CloudCog,
  Clock,
  Flag,
  GitBranch,
  Kanban,
  KeyRound,
  ListChecks,
  MessageCircleQuestion,
  MessageSquareQuote,
  MessageSquareReply,
  MessageSquareText,
  SendHorizontal,
  Tag,
  UserRoundCheck,
  Webhook,
  type LucideIcon,
} from 'lucide-react';
import type { CanalFluxo, Desenho, ProblemaFluxo, TipoCaixa } from '@malgoos/shared';
import type { TFunction } from 'i18next';
import type { CorSelo } from '../../componentes/base';
import { dataHora, textoLivre } from '../../nucleo/formato';

/** U14 F1: tipos da aba Automações (espelho dos DTOs de `modules/automacoes/src/fluxo/telas.ts`). */

export type EstadoFluxo = 'rascunho' | 'ativo' | 'pausado' | 'arquivado';
export type EstadoExecucaoFluxo = 'rodando' | 'aguardando' | 'concluida' | 'falhou' | 'cancelada';

export interface FluxoResumo {
  id: string;
  nome: string;
  canal: CanalFluxo;
  estado: EstadoFluxo;
  versao_numero: number | null;
  publicado_em: string | null;
  updated_at: string;
  ativas: number;
  execucoes_7d: number;
}

export interface Fluxo {
  id: string;
  nome: string;
  canal: CanalFluxo;
  estado: EstadoFluxo;
  rascunho: Desenho;
  revisao: number;
  versao_publicada: { id: string; numero: number; publicado_em: string; hash: string; canal: CanalFluxo } | null;
  publicado_em_dia: boolean;
  problemas: ProblemaFluxo[];
  tem_segredo: boolean;
  conversas: boolean;
}

export interface VersaoFluxo {
  id: string;
  numero: number;
  publicado_em: string;
  publicado_por_nome: string | null;
  caixas: number;
  atual: boolean;
}

export interface ExecucaoFluxo {
  id: string;
  versao_numero: number;
  /** U14 F2: nula na execução de comentário antes da resposta privada. */
  conversa_id: string | null;
  comentario_id: string | null;
  parceiro_nome: string | null;
  /** U14 F6: nulo na execução de evento de app externo (sem conversa). */
  canal: 'whatsapp' | 'instagram' | null;
  origem: 'gatilho' | 'botao' | 'teste' | 'comentario' | 'app_externo';
  estado: EstadoExecucaoFluxo;
  caixa_atual: string | null;
  espera_ate: string | null;
  passos: number;
  motivo: string | null;
  iniciada_em: string;
  terminada_em: string | null;
}

export interface PassoExecucao {
  ordem: number;
  caixa: string;
  tipo: TipoCaixa;
  resultado: 'ok' | 'erro' | 'aguardando';
  saida: string | null;
  /** Texto da opção quando a saída é um botão (o id da opção não aparece). */
  saida_rotulo?: string | null;
  detalhe: string | null;
  duracao_ms: number;
  created_at: string;
}

export const BASE_FLUXOS = '/automacoes/automacoes';
export const chaveFluxos = ['automacoes', 'fluxos'] as const;

export const COR_ESTADO_FLUXO: Record<EstadoFluxo, CorSelo> = { rascunho: 'neutro', ativo: 'verde', pausado: 'laranja', arquivado: 'neutro' };
export const COR_EXECUCAO: Record<EstadoExecucaoFluxo, CorSelo> = { rodando: 'azul', aguardando: 'ciano', concluida: 'verde', falhou: 'perigo', cancelada: 'neutro' };

export const ICONE_CAIXA: Record<TipoCaixa, LucideIcon> = {
  gatilho_palavra: KeyRound,
  gatilho_comentario: MessageSquareQuote,
  gatilho_broadcast: Megaphone,
  mensagem: MessageSquareText,
  botoes: ListChecks,
  pergunta: MessageCircleQuestion,
  resposta_publica: MessageSquareReply,
  resposta_privada: SendHorizontal,
  condicao: GitBranch,
  espera: Clock,
  etiqueta: Tag,
  transferir: UserRoundCheck,
  oportunidade: Kanban,
  webhook: Webhook,
  gatilho_app: CloudCog,
  app_externo: Blocks,
  fim: Flag,
};

/** Cor do selo do grupo da caixa (a mesma paleta dos selos do sistema). */
export const COR_GRUPO: Record<'gatilho' | 'mensagem' | 'logica' | 'acao', CorSelo> = { gatilho: 'acento', mensagem: 'azul', logica: 'roxo', acao: 'dourado' };

/** Caixas que o editor oferece na paleta (o gatilho já vem no fluxo novo). */
export const PALETA: { grupo: 'mensagem' | 'logica' | 'acao'; tipos: TipoCaixa[] }[] = [
  { grupo: 'mensagem', tipos: ['resposta_publica', 'resposta_privada', 'mensagem', 'botoes', 'pergunta'] },
  { grupo: 'logica', tipos: ['condicao', 'espera', 'fim'] },
  { grupo: 'acao', tipos: ['etiqueta', 'transferir', 'oportunidade', 'webhook', 'app_externo'] },
];
/** U14 F2: caixas que só existem no fluxo de comentário (a paleta esconde nos outros). */
export const SO_COMENTARIO: ReadonlySet<TipoCaixa> = new Set<TipoCaixa>(['resposta_publica', 'resposta_privada']);

const RE_CODIGO_TEXTO = /^[a-z_]+$/;

/**
 * Review r65 (VIS-B3): o motivo da execução é um código do motor (`fluxos.motivos.<código>`) ou o texto da recusa do
 * Conversas (pt, censurado pelo modo discreto).
 */
export function textoMotivo(t: TFunction<'automacoes'>, motivo: string): string {
  return RE_CODIGO_TEXTO.test(motivo) ? t(`fluxos.motivos.${motivo}`, { defaultValue: motivo }) : textoLivre(motivo);
}

/** O detalhe do passo: código (`fluxos.passos.<código>`), `<código>:<valor>` (a data da espera no fuso, a variável, o HTTP) ou texto. */
export function textoDetalhe(t: TFunction<'automacoes'>, detalhe: string): string {
  const m = /^([a-z_]+)(?::(.+))?$/.exec(detalhe);
  if (!m) return textoLivre(detalhe);
  const [, codigo, valor] = m;
  if (codigo === 'ate' && valor) return t('fluxos.passos.ate', { quando: dataHora(valor) });
  if (codigo === 'limite_hora' && valor) return t('fluxos.passos.limite_hora', { quando: dataHora(valor) });
  if (codigo === 'http' && valor) return t('fluxos.passos.http', { status: valor });
  return t(`fluxos.passos.${codigo}`, { defaultValue: textoLivre(detalhe), valor: valor ?? '' });
}
